import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { HEADER_ICON_BUTTON } from '@/shared/ui-class';
import { cn } from '@/shared/cn';

/** Opaque admin modal chrome — form interior, not glass. */
export const ADMIN_MODAL_CARD =
  'rounded-2xl border border-divider bg-white p-6 shadow-lg dark:bg-zinc-950';

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  placement?: 'center' | 'right';
  className?: string;
};

export function Modal({
  open,
  onOpenChange,
  title,
  children,
  placement = 'center',
  className,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const frame = requestAnimationFrame(() => {
      panelRef.current?.focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus();
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close dialog"
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          ADMIN_MODAL_CARD,
          'fixed z-50 overflow-y-auto focus:outline-none',
          placement === 'center' && [
            'max-h-[calc(100vh-2rem)] left-1/2 top-1/2 w-[min(480px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2',
          ],
          placement === 'right' && [
            'right-0 top-0 flex h-full w-full max-w-md flex-col overflow-hidden p-0',
            'rounded-none rounded-l-2xl',
          ],
          className,
        )}
      >
        <div
          className={cn(
            'flex items-start justify-between gap-4',
            placement === 'right' && 'shrink-0 border-b border-divider px-6 py-4',
          )}
        >
          <h2
            id={titleId}
            className="text-base font-semibold text-zinc-900 dark:text-zinc-100"
          >
            {title}
          </h2>
          <button
            type="button"
            className={HEADER_ICON_BUTTON}
            aria-label="Close"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div
          className={cn(
            placement === 'center' && 'mt-4',
            placement === 'right' && 'flex min-h-0 flex-1 flex-col',
          )}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
